import React from 'react';
import { cn } from '@/utils/cn';
import SecurityFeatureCard from '@/components/common/Card/SecurityFeatureCard';
import ComplianceBadgeCard from '@/components/common/Card/ComplianceBadgeCard';

interface SecurityFeature {
  icon: string;
  title: string;
  description: string;
}

interface ComplianceBadge {
  logo: string;
  title: string;
}

interface BentoGridProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle: string;
  securityFeatures: SecurityFeature[];
  complianceTitle: string;
  complianceDescription: string;
  complianceBadges: ComplianceBadge[];
}

const BentoGrid = ({
  className,
  title,
  subtitle,
  securityFeatures,
  complianceTitle,
  complianceDescription,
  complianceBadges,
  ...props
}: BentoGridProps) => {
  return (
    <section className={cn("py-12 md:py-20 px-4 bg-black text-white", className)} {...props}>
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">{title}</h2>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">{subtitle}</p>
        </div>

        {/* Security Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10">
          {securityFeatures.map((feature, index) => (
            <SecurityFeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>

        {/* Compliance Section */}
        <div className="mt-12 ">
          {/* Mobile view - stacks badges in grid */}
          <div className="md:hidden">
            <div className="text-center mb-8">
              <h3 className="text-xl md:text-2xl font-semibold mb-2">{complianceTitle}</h3>
              <p className="text-base text-gray-400 max-w-2xl mx-auto">{complianceDescription}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {complianceBadges.map((badge, index) => (
                <ComplianceBadgeCard
                  key={index}
                  logo={badge.logo}
                  title={badge.title}
                  backgroundColor="#1A1A1A"
                  className="flex flex-col items-center"
                />
              ))}
            </div>
          </div>

          {/* Desktop view with badges on sides and content in the middle */}
          <div className="hidden md:block rounded-xl bg-[#1A1A1A] p-6 md:p-16">
            <div className='flex items-center justify-between'>
              <ComplianceBadgeCard
                logo={complianceBadges[0].logo}
                title={complianceBadges[0].title}
                iconWidth={120}
                iconHeight={120}
              />
              <ComplianceBadgeCard
                logo={complianceBadges[1].logo}
                title={complianceBadges[1].title}
                iconWidth={120}
                iconHeight={120}
              />

              <div className="text-center max-w-sm">
                <h3 className="text-xl md:text-2xl font-semibold mb-4">{complianceTitle}</h3>
                <p className="text-base text-gray-400">{complianceDescription}</p>
              </div>

              <ComplianceBadgeCard
                logo={complianceBadges[2].logo}
                title={complianceBadges[2].title}
                iconWidth={120}
                iconHeight={120}
              />
              <ComplianceBadgeCard
                logo={complianceBadges[3].logo}
                title={complianceBadges[3].title}
                iconWidth={120}
                iconHeight={120}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BentoGrid;